import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const url = original.url || "";
    if (error.response?.status === 401 && !original._retry && !url.startsWith("/auth/") && !url.startsWith("/kid")) {
      original._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh").finally(() => (refreshing = null));
        await refreshing;
        return api(original);
      } catch {
        window.dispatchEvent(new Event("auth:expired"));
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (typeof e?.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (typeof detail?.msg === "string") return detail.msg;
  return String(detail);
}

const data = (p) => p.then((r) => r.data);

export const authApi = {
  me: () => data(api.get("/auth/me")),
  refresh: () => data(api.post("/auth/refresh")),
  login: (email, password) => data(api.post("/auth/login", { email, password })),
  register: (name, email, password, invite) => data(api.post("/auth/register", { name, email, password, ...(invite ? { invite } : {}) })),
  logout: () => data(api.post("/auth/logout")),
  logoutAll: () => data(api.post("/auth/logout-all")),
  updateMe: (payload) => data(api.patch("/auth/me", payload)),
  changePassword: (current_password, new_password) => data(api.post("/auth/change-password", { current_password, new_password })),
  checkReset: (token) => data(api.get(`/auth/reset/${token}`)),
  resetPassword: (token, password) => data(api.post("/auth/reset-password", { token, password })),
  checkInvite: (token) => data(api.get(`/auth/invite/${token}`)),
};
export const adminResetLink = (id) => data(api.post(`/users/${id}/reset-link`));
export const listInvites = () => data(api.get("/invites"));
export const createInvite = (p) => data(api.post("/invites", p));
export const revokeInvite = (id) => data(api.delete(`/invites/${id}`));

export const listUsers = () => data(api.get("/users"));
export const listComments = (plantId) => data(api.get(`/plants/${plantId}/comments`));
export const addComment = (plantId, payload) => data(api.post(`/plants/${plantId}/comments`, payload));
export const deleteComment = (id) => data(api.delete(`/comments/${id}`));
export const listTasks = (params = {}) => data(api.get("/tasks", { params }));
export const createTask = (payload) => data(api.post("/tasks", payload));
export const updateTask = (id, payload) => data(api.patch(`/tasks/${id}`, payload));
export const deleteTask = (id) => data(api.delete(`/tasks/${id}`));
export const restoreTask = (id) => data(api.post(`/tasks/${id}/restore`));
export const toggleFavorite = (id) => data(api.post(`/plants/${id}/favorite`));
export const listOpenCritical = () => data(api.get("/critical/open"));
export const ackCritical = () => data(api.post("/critical/ack"));
export const adminUpdateUser = (id, p) => data(api.patch(`/users/${id}`, p));
export const userActivity = (id) => data(api.get(`/users/${id}/activity`));
export const editEvent = (id, p) => data(api.patch(`/events/${id}`, p));
export const adminDeleteUser = (id) => data(api.delete(`/users/${id}`));
export const setUserRole = (id, role) => data(api.patch(`/users/${id}/role`, { role }));
export const listProps = (params = {}) => data(api.get("/propagations", { params }));
export const createProp = (p) => data(api.post("/propagations", p));
export const updateProp = (id, p) => data(api.patch(`/propagations/${id}`, p));
export const promoteProp = (id, payload = {}) => data(api.post(`/propagations/${id}/promote`, payload));
export const deleteProp = (id) => data(api.delete(`/propagations/${id}`));
export const getProp = (id) => data(api.get(`/propagations/${id}`));
export const addPropJournal = (id, p) => data(api.post(`/propagations/${id}/journal`, p));
export const deletePropJournal = (id, entryId) => data(api.delete(`/propagations/${id}/journal/${entryId}`));
export const listPhotos = (params = {}) => data(api.get("/photos", { params }));
export const photoMeta = (path) => data(api.get("/photos/meta", { params: { path } }));
export const deletePhotos = (paths) => data(api.post("/photos/delete", { paths }));

export const fileUrl = (path, w) => (path ? `${API}/files/${path}${w ? `?w=${w}` : ""}` : null);

export const listStreaks = () => data(api.get("/streaks"));
export const getLocationIcons = () => data(api.get("/location-icons"));
export const listLocations = () => data(api.get("/locations"));
export const renameLocation = (name, to) => data(api.post("/locations/rename", { name, to }));
export const setLocationIcon = (name, icon) => data(api.put("/location-icons", { name, icon }));
export const listCustomFields = () => data(api.get("/custom-fields"));
export const createCustomField = (f) => data(api.post("/custom-fields", f));
export const updateCustomField = (id, f) => data(api.patch(`/custom-fields/${id}`, f));
export const deleteCustomField = (id) => data(api.delete(`/custom-fields/${id}`));
export const listProducts = (kind = "fertilizer") => data(api.get("/care-products", { params: { kind } }));
export const createProduct = (name, kind = "fertilizer") => data(api.post("/care-products", { name, kind }));
export const deleteProduct = (id) => data(api.delete(`/care-products/${id}`));

export const focusStyle = (f) => (f ? { objectPosition: `${f.x}% ${f.y}%`, transform: `scale(${f.zoom || 1})`, transformOrigin: `${f.x}% ${f.y}%` } : undefined);

export async function shrink(file, max = 2048) {
  if (!/^image\/(jpeg|png|webp|heic|heif)/.test(file.type) || file.size < 900_000) return file;
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
    const s = Math.min(1, max / Math.max(bmp.width, bmp.height));
    const c = document.createElement("canvas");
    c.width = Math.round(bmp.width * s);
    c.height = Math.round(bmp.height * s);
    c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
    const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", 0.86));
    return blob && blob.size < file.size ? new File([blob], file.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" }) : file;
  } catch {
    return file;
  }
}

export async function uploadPhoto(raw) {
  const file = await shrink(raw);
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data; // { path, content_type, size }
}

export async function createPlant(payload) {
  const { data } = await api.post("/plants", payload);
  return data;
}

export async function listPlants(params = {}) {
  const { data } = await api.get("/plants", { params });
  return data;
}

export async function getPlant(id) {
  const { data } = await api.get(`/plants/${id}`);
  return data;
}

export async function updatePlant(id, payload) {
  const { data } = await api.patch(`/plants/${id}`, payload);
  return data;
}

export async function deletePlant(id) {
  const { data } = await api.delete(`/plants/${id}`);
  return data;
}

export async function listEvents(plantId) {
  const { data } = await api.get(`/plants/${plantId}/events`);
  return data;
}

export async function createEvent(plantId, payload) {
  const { data } = await api.post(`/plants/${plantId}/events`, payload);
  return data;
}

export async function deleteEvent(id) {
  const { data } = await api.delete(`/events/${id}`);
  return data;
}

export async function resolveEvent(id, outcome) {
  const { data } = await api.post(`/events/${id}/resolve`, null, {
    params: outcome ? { outcome } : {},
  });
  return data;
}

export async function batchEvents(payload) {
  const { data } = await api.post("/events/batch", payload);
  return data;
}

export async function undoBatch(batchId) {
  const { data } = await api.delete(`/events/batch/${batchId}`);
  return data;
}


export async function getLastEvents() {
  const { data } = await api.get("/last-events");
  return data;
}

export async function getStats() {
  const { data } = await api.get("/stats");
  return data;
}

export const listReminders = (params = {}) => data(api.get("/reminders", { params }));
export const savePlantTip = (id, text) => data(api.put(`/plants/${id}/tip`, { text }));
export const setWinterCare = (id, body) => data(api.put(`/plants/${id}/winter-care`, body));
export const clearWinterCare = (id) => data(api.delete(`/plants/${id}/winter-care`));
export const listOrderSets = () => data(api.get("/order-sets"));
export const saveOrderSet = (s) => data(s.id ? api.put(`/order-sets/${s.id}`, s) : api.post("/order-sets", s));
export const deleteOrderSet = (id) => data(api.delete(`/order-sets/${id}`));
export const listCaseOrders = (caseId) => data(api.get(`/cases/${caseId}/orders`));
export const placeOrders = (caseId, body) => data(api.post(`/cases/${caseId}/orders`, body));
export const updateOrder = (id, status) => data(api.patch(`/orders/${id}`, { status }));
export const listOrderLibrary = () => data(api.get("/order-library"));
export const chartOccurrence = (id, date, done) => data(api.post(`/orders/${id}/occurrences`, { date, done }));
export const completeOnboarding = () => data(api.post("/onboarding/complete"));
export const listAnnouncements = (params = {}) => data(api.get("/announcements", { params }));
export const createAnnouncement = (payload) => data(api.post("/announcements", payload));
export const updateAnnouncement = (id, payload) => data(api.patch(`/announcements/${id}`, payload));
export const deleteAnnouncement = (id) => data(api.delete(`/announcements/${id}`));
export const dismissAnnouncement = (id) => data(api.post(`/announcements/${id}/dismiss`));
export const getCase = (id) => data(api.get(`/cases/${id}`));
export const listPlantCases = (plantId) => data(api.get(`/plants/${plantId}/cases`));
export const addCaseUpdate = (id, payload) => data(api.post(`/cases/${id}/updates`, payload));
export const deleteCase = (id) => data(api.delete(`/cases/${id}`));

export const listNotifications = () => data(api.get("/notifications"));
export const unreadNotifications = () => data(api.get("/notifications/unread-count"));
export const readNotification = (id) => data(api.post(`/notifications/${id}/read`));
export const readAllNotifications = () => data(api.post("/notifications/read-all"));
export const getHiddenOptions = () => data(api.get("/options/hidden"));
export const setHiddenOption = (field, value, restore = false) => data(api.post("/options/hidden", { field, value }, { params: restore ? { restore: true } : {} }));
export const getMySummary = () => data(api.get("/me/summary"));
